import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode, useEffect, useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth, OptionContentType, OptionData} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Section from 'ui-library/menus/Section';

/**
 * Pick from a list of options in a cell value. Unlike OptionCellDropdown, this adds
 * a SearchableMenu to the dropdown.
 */
export interface OptionSearchCellDropdownProps {
  isSelected: boolean;
  options: Array<string | OptionData>;
  onOpenDropdown?: (() => void) | null;
  onUpdate: (selectedOption: string) => void;
  emptyOptionsDisplay?: ReactNode;
  staticDisplay?: string | ReactNode;
  fullWidth?: boolean;
  lightHoverBackground?: boolean;
}

export default function OptionSearchCellDropdown(props: OptionSearchCellDropdownProps): JSX.Element {
  const [isOpen, setOpen] = useState(props.isSelected);

  useEffect(() => {
    if (!props.isSelected) {
      setOpen(false);
    }
  }, [props.isSelected]);

  function _handleOpenMenu(): void {
    props.onOpenDropdown?.(); // Select the cell when its menu is opened to re-inforce editing the cell
    setOpen(true);
  }

  const getSearchableMenuOptions = (options: Array<string | OptionData>): Array<OptionData> =>
    options.map((option) => (typeof option === 'string' ? {name: option, value: option} : option));

  let fullWidthClickHandler;
  if (props.fullWidth) {
    fullWidthClickHandler = _handleOpenMenu;
  }

  const searchableMenu =
    props.options.length === 0 && props.emptyOptionsDisplay ? (
      <Menu
        width={MenuWidth.LARGE}
        scrollable={true}
        className={classNames('selectorDropdown-list dropdown-menu scrollable pull-right')}
      >
        <Section>
          <Option isDisabled={false} isSelected={false} contentType={OptionContentType.PLAIN}>
            {props.emptyOptionsDisplay}
          </Option>
        </Section>
      </Menu>
    ) : (
      <SearchableMenu
        width={MenuWidth.FULL}
        optionDatas={getSearchableMenuOptions(props.options)}
        onSelect={(option) => {
          props.onUpdate(option);
        }}
        scrollable={true}
        className={classNames('selectorDropdown-list dropdown-menu pull-right')}
      />
    );

  return (
    <div
      className={classNames('u-pointer u-flex-display', css(styles.optionSearchSelectorContainer))}
      onClick={fullWidthClickHandler}
      onDoubleClick={(e) => {
        e.stopPropagation();
      }}
    >
      <span className={classNames('u-flex-grow', css(styles.displayContents))}>
        {props.staticDisplay || (props.fullWidth ? '\u00A0' : '')}
      </span>
      <div className="selectorDropdown">
        <AbsoluteDropdownContainer
          onRequestClose={() => {
            setOpen(false);
          }}
          open={isOpen}
          shouldCloseOnInsideClick={true}
          menu={searchableMenu}
        >
          <BenchlingIcon
            className={classNames('selectorDropdown-button', {'is-open': isOpen})}
            icon="chevron-down"
            onClick={(e) => {
              e.stopPropagation();
              _handleOpenMenu();
            }}
            {...getTestProps({component: 'OptionSearchCellDropdown', element: 'toggle'})}
          />
        </AbsoluteDropdownContainer>
      </div>
    </div>
  );
}

export const styles = StyleSheet.create({
  optionSearchSelectorContainer: {
    position: 'relative',
  },
  displayContents: {
    overflow: 'hidden',
    marginRight: 14,
  },
});
